import { AuthorizedApiBase } from "./authorized-api-base";
import { ApiException, ServiceMessage, ProblemDetails } from "./taxportal-client";
import { IsRolloverMicroserviceEnabled } from "../envVariables";

export interface GetUserRolloverDTO {
  id: number;
  executionDate: string;
  userId: number;
  userName?: string;
  status: string;
  statusDetails: string;
  processType: string;
  rollOverType: string;
  processTrackId: number;
}
export interface GetUserRolloverResponse {
  rollOverScheduleDays: number;
  mdmClientId: number;
  hQRolloversTracks: GetUserRolloverDTO[];
  rolloverOptions: any[];
  taxYear?: string;
  newTaxYear?: string;
  appName: string;
}

export interface RolloverFilesDTO {
  folderName: string;
  folderLink: string;
  entity: string;
  files: RolloverFilesList[];
}

export interface RolloverFilesList {
  fileName: string;
  fileLink: string;
}

export interface RolloverStatusDetailsDTO {
  rolloverID: number;
  rolloverinformationList: RolloverStatusDetails[];
}

export interface RolloverStatusDetails {
  id?: number;
  hqRolloversTrackId?: number;
  hqSiteId?: number;
  entityId?: string | null;
  entityName?: string | null;
  step?: string;
  status?: string;
  statusMessage?: string;
  isActive?: boolean;
  created?: string;
  createdBy?: string;
  lastModified?: string;
  lastModifiedBy?: string;
}
export interface GetRolloverCapacityResponse {
  rolloverDate: Date;
  rejectedDate: number;
}

export interface RolloverRequestMessage {
  hqSiteId?: number;
  taxYear?: number;
  userName?: string;
  correlationId?: string;
  rolloverStepName?: string;
  archiveFolderId?: number | undefined;
  hqRolloversTracksId?: number | undefined;
  mdmClientId?: number | undefined;
  appName?: string;
  newTaxYear?: number | undefined;
  filesToDelete?: string[];
  isheetName?: string;
  isLastBatch?: boolean | undefined;
  isFSTemplate?: boolean | undefined;
}

export interface RetryRolloverResponse {
  isSuccess: boolean;
  nextStep?: string;
  message?: string;
}

export enum RolloverEmailNotification {
  ROLLOVER_INITIATED = 0,
  REMINDER = 1,
  CANCELED = 2,
}
export interface NotificationActionRequest {
  hqSiteId: number;
  rolloverTrackId: number;
  notificationType: RolloverEmailNotification;
  userName: string;
  isResend: boolean;
  isCancel: boolean;
}

export interface EmailDetailsDto {
  emailType: string;
  scheduledDate: string | null;
  recipients: string[];
  status: string;
  canResend: boolean;
  canCancel: boolean;
  notes: string;
}

export interface ArchivedFileRestoreRequest {
  fileId?: number;
  rolloversTrackDocumentDetailId: number;
  fileName?: string;
  folderName?: string;
  fileSizeInBytes?: number;
  formattedFileSize?: string;
  restoreFileStatus?: string;
  restoreFileMessage?: string;
  zipName?: string;
  dmsMetadataFileId?: string;
}

export interface RestoreArchiveFilesResponse {
  isSuccess: boolean;
  statusMessage?: string;
}

export interface RestoreArchiveFileItem {
  fileId: number;
  fileName: string,
  zipName: string;
  rolloverTrackDocumentId: number;
  dMSMetadataFileId: string;
  folderName: string;
  formattedFileSize: string;
  restoreStatus: string;
}

export interface RestoreArchiveFilesRequest {
  rolloverTrackId: number;
  taxYear: number;
  SelectedFiles: RestoreArchiveFileItem[];
  siteId: number;
  userName: string | null;
}

export interface RolloverInfoRequest {
  hqSiteId: number;
  taxYear: number;
  initiatedBy: string;
}

export interface RolloverInfoRow {
  rolloverRetrivalId?: number;
  initiated?: string;
  initiatedBy?: string;
  hqSiteId?: number;
  taxYear?: number;
  status?: string;
  message?: string;
  isActive?: boolean;
  created?: string;
  createdBy?: string;
  lastModified?: string;
  lastModifiedBy?: string;
}

export interface RetrievalDetailRow {
  rolloverRetrivalDetailId?: number;
  process?: string;
  status?: string;
  message?: string;
  rolloverRetrivalId?: number;
  isActive?: boolean;
  created?: string;
  createdBy?: string;
  lastModified?: string;
  lastModifiedBy?: string;
}

export interface RetrievalUploadRow {
  dateRestore?: string;
  initiatedBy?: string;
  fileName?: string;
  folderName?: string;
  fileSize?: string;
  zipName?: string;
  restoreStatus?: string | null;
}

export class RolloverServiceClient extends AuthorizedApiBase {
  private http: {
    fetch(url: RequestInfo, init?: RequestInit): Promise<Response>;
  };
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

  constructor(baseUrl?: string, http?: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> }) {
    super();
    this.http = http ? http : (window as any);
    this.baseUrl = baseUrl ?? "";
  }

  taxPortal_GetAllRolloverCapacity(
    rolloverType: string,
    appName: string
  ): Promise<GetRolloverCapacityResponse[]> {
    //console.log("Scheduling rollover with date:", date);

    let url_ =
      this.baseUrl +
      (IsRolloverMicroserviceEnabled === "true"
        ? `/api/v1/Schedule/ScheduleCapacity?rolloverType=${rolloverType}&appName=${appName}`
        : null);
    if (rolloverType === undefined || rolloverType === null)
      throw new Error(
        "The parameter 'rolloverType' must be defined and cannot be null."
      );
    const urlApi = `${url_}`;

    let options_: RequestInit = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => {
        return this.http.fetch(urlApi, transformedOptions_);
      })
      .then((_response: Response) => {
        return _response.text().then((_responseText) => {
          let result200: any = null;
          result200 =
            _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as GetRolloverCapacityResponse[]);
          return result200;
        });
      });
  }

  // Rollover Grid API Call
  taxPortal_GetAllRolloverInfo(hqSiteId: number): Promise<GetUserRolloverResponse> {
    let url_ =
      this.baseUrl +
      (IsRolloverMicroserviceEnabled === "true"
        ? "/api/v1/RolloverTrackDetails/GetRolloverTracks?siteId="
        : "/api/rollover/");
    if (hqSiteId === undefined || hqSiteId === null)
      throw new Error("The parameter 'hqSiteId' must be defined and cannot be null.");
    const urlApi = `${url_}${hqSiteId}`;

    let options_: RequestInit = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => {
        return this.http.fetch(urlApi, transformedOptions_);
      })
      .then((_response: Response) => {
        return this.processTaxPortal_GetAllRollovers(_response);
      });
  }

  // Rollover Details API Call
  taxPortal_GetRollOverInformation(rolloverId: number): Promise<RolloverStatusDetailsDTO> {
    if (rolloverId === undefined || rolloverId === null) throw new Error("The parameter 'rolloverId' must be defined.");
    let url_ =
      this.baseUrl +
      (IsRolloverMicroserviceEnabled === "true"
        ? `/api/v1/RolloverTrackDetails/GetRolloverTracksDetails?rolloverID=${rolloverId}`
        : `/api/GetRollOverInformation/${rolloverId}`);
    url_ = url_.replace(/[?&]$/, "");

    let options_: RequestInit = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => {
        return this.http.fetch(url_, transformedOptions_);
      })
      .then((_response: Response) => {
        return this.processTaxPortal_GetRollOverInformation(_response);
      });
  }

  // Get rollover file details
  taxPortal_GetRolloverFiles(fileId: number): Promise<RolloverFilesDTO[]> {
    let url_ =
      this.baseUrl +
      (IsRolloverMicroserviceEnabled === "true"
        ? `/api/v1/RolloverTrackDetails/GetRolloversTrackDocumentDetail?rolloverId=${fileId}`
        : `/api/rollover/filesDetail/${fileId}`);
    if (fileId === undefined || fileId === null) throw new Error("The parameter 'fileId' must be defined.");
    url_ = url_.replace("{fileId}", encodeURIComponent("" + fileId));
    url_ = url_.replace(/[?&]$/, "");

    let options_: RequestInit = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => {
        return this.http.fetch(url_, transformedOptions_);
      })
      .then((_response: Response) => {
        return this.processTaxPortal_GetRolloverFiles(_response);
      });
  }

  // Rollover Schedule
  taxPortal_ScheduleRollover(
    siteId: number,
    rollOverType: number,
    userName?: string,
    selectedDate?: string,
    messages?: ServiceMessage[],
    currentTaxYear?: any,
    newTaxYear?: any,
    appName?: string,
    firstNotificationDate?: string,
    secondNotificationDate?: string,
    cancelationEmail?: string
  ): Promise<string> {
    if (siteId === undefined || siteId === null)
      throw new Error("The parameter 'siteId' must be defined.");

    const params = new URLSearchParams({
      userName: userName ?? "RSMTaxPortalAdmin",
      scheduleDate: selectedDate ?? "",
      currentTaxYear: currentTaxYear != null ? String(currentTaxYear) : "",
      newTaxYear: newTaxYear != null ? String(newTaxYear) : "",
      rollOverType: rollOverType != null ? String(rollOverType) : "",
      appName: appName ?? "",
      firstNotificationDate: firstNotificationDate ?? "",
      secondNotificationDate: secondNotificationDate ?? "",
      cancelationEmail: cancelationEmail ?? ""
    });
    // let url_ =
    //   this.baseUrl +
    //   (IsRolloverMicroserviceEnabled === "true"
    //     ? `/api/v1/RolloverTracks/ScheduleRolloverFiles/${siteId}?userName=${userName}&selectedDate=${selectedDate?.toISOString()}&currentTaxYear=${currentTaxYear}&newTaxYear=${newTaxYear}`
    //     : `/api/rollover/schedule/${siteId}?rolloverType=FILES&currentTaxYear=${currentTaxYear}&newTaxYear=${newTaxYear}`);
    let url_ =
      this.baseUrl +
      (IsRolloverMicroserviceEnabled === "true"
        ? `/api/v1/RolloverTracks/ScheduleRolloverFiles/${siteId}?${params.toString()}`
        : `/api/rollover/schedule/${siteId}?rolloverType=FILES&currentTaxYear=${currentTaxYear}&newTaxYear=${newTaxYear}`);

    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(messages);

    let options_: RequestInit = {
      body: content_,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => {
        return this.http.fetch(url_, transformedOptions_);
      })
      .then((_response: Response) => {
        return this.processtaxPortal_ScheduleRollover(_response);
      });
  }

  // Rollover Cancel Schedule
  taxPortal_CancelScheduledRollover(
    siteId: number,
    rolloverId: number,
    messages?: ServiceMessage[],
    userName?: string
  ): Promise<string> {
    if (siteId === undefined || siteId === null) throw new Error("The parameter 'siteId' must be defined.");
    let url_ =
      IsRolloverMicroserviceEnabled === "true"
        ? `${this.baseUrl}/api/v1/RolloverTracks/CancelScheduledRollover/${siteId}?rolloverId=${rolloverId}&userName=${userName}`
        : `${this.baseUrl}/api/rollover/schedule/${siteId}/cancel/${rolloverId}`;
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(messages);

    let options_: RequestInit = {
      body: content_,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => {
        return this.http.fetch(url_, transformedOptions_);
      })
      .then((_response: Response) => {
        return this.processtaxPortal_CancelScheduledRollover(_response);
      });
  }

  taxPortal_ScheduleSiteRolloverTrack(hqRolloverTrackId: number): Promise<boolean> {
    let url_ = this.baseUrl + "/api/v1/Schedule/ScheduleSiteRolloverTrack/" + hqRolloverTrackId;
    url_ = url_.replace(/[?&]$/, "");
    const content_ = JSON.stringify({});

    let options_: RequestInit = {
      body: content_,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => {
        return this.http.fetch(url_, transformedOptions_);
      })
      .then((_response: Response) => {
        return this.processTaxPortal_ScheduleSiteRolloverTrack(_response);
      });
  }

  protected processTaxPortal_ScheduleSiteRolloverTrack(response: Response): Promise<boolean> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }
    if (status === 200) {
      return response.text().then((_responseText) => {
        let result200: any = null;
        result200 =
          _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as boolean);
        return result200;
      });
    } else if (status === 400) {
      return response.text().then((_responseText) => {
        let result400: any = null;
        result400 = _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as RetryRolloverResponse);
        return throwException("A server side error occurred.", status, _responseText, _headers, result400);
      });
    } else if (status === 500) {
      return response.text().then((_responseText) => {
        return throwException("A server side error occurred.", status, _responseText, _headers);
      });
    } else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      });
    }
    return Promise.resolve<boolean>(false as any);
  }

  // Retry Rollover API Call
  taxPortal_RetryRollover(message: RolloverRequestMessage): Promise<RetryRolloverResponse> {
    let url_ = this.baseUrl + "/api/v1/ArchiveFile/RetryRollover";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(message);

    let options_: RequestInit = {
      body: content_,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => {
        return this.http.fetch(url_, transformedOptions_);
      })
      .then((_response: Response) => {
        return this.processTaxPortal_RetryRollover(_response);
      });
  }

  protected processTaxPortal_RetryRollover(response: Response): Promise<RetryRolloverResponse> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }
    if (status === 200) {
      return response.text().then((_responseText) => {
        let result200: any = null;
        result200 =
          _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as RetryRolloverResponse);
        return result200;
      });
    } else if (status === 400) {
      return response.text().then((_responseText) => {
        let result400: any = null;
        result400 = _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as RetryRolloverResponse);
        return throwException("A server side error occurred.", status, _responseText, _headers, result400);
      });
    } else if (status === 500) {
      return response.text().then((_responseText) => {
        return throwException("A server side error occurred.", status, _responseText, _headers);
      });
    } else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      });
    }
    return Promise.resolve<RetryRolloverResponse>(null as any);
  }

  protected processTaxPortal_GetRolloverCapacity(
    response: Response
  ): Promise<GetRolloverCapacityResponse> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }
    if (status === 200) {
      return response.text().then((_responseText) => {
        let result200: any = null;
        result200 =
          _responseText === ""
            ? null
            : (JSON.parse(
              _responseText,
              this.jsonParseReviver
            ) as GetRolloverCapacityResponse);
        return result200;
      });
    } else if (status === 400) {
      return response.text().then((_responseText) => {
        return throwException(
          "A server side error occurred.",
          status,
          _responseText,
          _headers
        );
      });
    } else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwException(
          "An unexpected server error occurred.",
          status,
          _responseText,
          _headers
        );
      });
    }
    return Promise.resolve<GetRolloverCapacityResponse>(null as any);
  }

  protected processTaxPortal_GetRolloverFiles(
    response: Response
  ): Promise<RolloverFilesDTO[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }
    if (status === 200) {
      return response.text().then((_responseText) => {
        let result200: any = null;
        result200 =
          _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as RolloverFilesDTO[]);
        return result200;
      });
    } else if (status === 400) {
      return response.text().then((_responseText) => {
        return throwException("A server side error occurred.", status, _responseText, _headers);
      });
    } else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      });
    }
    return Promise.resolve<RolloverFilesDTO[]>(null as any);
  }

  protected processtaxPortal_ScheduleRollover(response: Response): Promise<string> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }
    if (status === 200) {
      return response.text().then((_responseText) => {
        let result200: any = null;
        result200 = _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as string);
        return result200;
      });
    } else if (status === 400) {
      return response.text().then((_responseText) => {
        return throwException("A server side error occurred.", status, _responseText, _headers);
      });
    } else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      });
    }
    return Promise.resolve<string>(null as any);
  }

  protected processtaxPortal_CancelScheduledRollover(response: Response): Promise<string> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }
    if (status === 200) {
      return response.text().then((_responseText) => {
        let result200: any = null;
        result200 = _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as string);
        return result200;
      });
    } else if (status === 400) {
      return response.text().then((_responseText) => {
        return throwException("A server side error occurred.", status, _responseText, _headers);
      });
    } else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      });
    }
    return Promise.resolve<string>(null as any);
  }

  protected processTaxPortal_GetRollOverInformation(response: Response): Promise<RolloverStatusDetailsDTO> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }
    if (status === 200) {
      return response.text().then((_responseText) => {
        let result200: any = null;
        result200 =
          _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as RolloverStatusDetailsDTO);
        return result200;
      });
    } else if (status === 400) {
      return response.text().then((_responseText) => {
        return throwException("A server side error occurred.", status, _responseText, _headers);
      });
    } else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      });
    }
    return Promise.resolve<RolloverStatusDetailsDTO>(null as any);
  }

  protected processTaxPortal_GetAllRollovers(response: Response): Promise<GetUserRolloverResponse> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }
    if (status === 200) {
      return response.text().then((_responseText) => {
        let result200: any = null;
        result200 =
          _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as GetUserRolloverResponse);
        return result200;
      });
    } else if (status === 400) {
      return response.text().then((_responseText) => {
        return throwException("A server side error occurred.", status, _responseText, _headers);
      });
    } else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      });
    }
    return Promise.resolve<GetUserRolloverResponse>(null as any);
  }

  removeArchive(siteId: number | undefined, currentTaxYear: number | undefined, signal?: AbortSignal): Promise<void> {
    console.log("Removing rollover archive for siteId:", siteId, "and currentTaxYear:", currentTaxYear);
    console.log(this.baseUrl); // = "https://localhost:7142";
    let url_ = this.baseUrl + "/api/v1/ArchiveFile/RemoveRollover?";
    if (siteId === null) throw new Error("The parameter 'siteId' cannot be null.");
    else if (siteId !== undefined) url_ += "SiteId=" + encodeURIComponent("" + siteId) + "&";
    if (currentTaxYear === null) throw new Error("The parameter 'currentTaxYear' cannot be null.");
    else if (currentTaxYear !== undefined) url_ += "currentTaxYear=" + encodeURIComponent("" + currentTaxYear) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: RequestInit = {
      method: "DELETE",
      signal,
      headers: {},
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => {
        return this.http.fetch(url_, transformedOptions_);
      })
      .then((_response: Response) => {
        return this.processRollover_RemoveArchive(_response);
      });
  }

  protected processRollover_RemoveArchive(response: Response): Promise<void> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }
    if (status === 200) {
      return response.text().then((_responseText) => {
        return;
      });
    } else if (status === 400) {
      return response.text().then((_responseText) => {
        let result400: any = null;
        result400 = _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as ProblemDetails);
        return throwException("A server side error occurred.", status, _responseText, _headers, result400);
      });
    } else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      });
    }
    return Promise.resolve<void>(null as any);
  }

  archiveFile_InsertTopic(requestData: RolloverRequestMessage, signal?: AbortSignal): Promise<void> {
    let url_ = this.baseUrl + "/api/v1/ArchiveFile/InsertTopic";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(requestData);

    let options_: RequestInit = {
      body: content_,
      method: "POST",
      signal,
      headers: {
        "Content-Type": "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => {
        return this.http.fetch(url_, transformedOptions_);
      })
      .then((_response: Response) => {
        return this.processArchiveFile_InsertTopic(_response);
      });
  }

  protected processArchiveFile_InsertTopic(response: Response): Promise<void> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }
    if (status === 200) {
      return response.text().then((_responseText) => {
        return;
      });
    } else if (status === 400) {
      return response.text().then((_responseText) => {
        let result400: any = null;
        result400 = _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as ProblemDetails);
        return throwException("A server side error occurred.", status, _responseText, _headers, result400);
      });
    } else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      });
    }
    return Promise.resolve<void>(null as any);
  }

  archiveFile_UpdCopiedSrcFolderId(
    siteId: number | undefined,
    year: number | undefined,
    signal?: AbortSignal
  ): Promise<void> {
    let url_ = this.baseUrl + "/api/v1/ArchiveFile/UpdCopiedSrcFolderId?";
    if (siteId === null) throw new Error("The parameter 'siteId' cannot be null.");
    else if (siteId !== undefined) url_ += "siteId=" + encodeURIComponent("" + siteId) + "&";
    if (year === null) throw new Error("The parameter 'year' cannot be null.");
    else if (year !== undefined) url_ += "year=" + encodeURIComponent("" + year) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: RequestInit = {
      method: "GET",
      signal,
      headers: {},
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => {
        return this.http.fetch(url_, transformedOptions_);
      })
      .then((_response: Response) => {
        return this.processArchiveFile_UpdCopiedSrcFolderId(_response);
      });
  }

  protected processArchiveFile_UpdCopiedSrcFolderId(response: Response): Promise<void> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }
    if (status === 200) {
      return response.text().then((_responseText) => {
        return;
      });
    } else if (status === 400) {
      return response.text().then((_responseText) => {
        let result400: any = null;
        result400 = _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as ProblemDetails);
        return throwException("A server side error occurred.", status, _responseText, _headers, result400);
      });
    } else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      });
    }
    return Promise.resolve<void>(null as any);
  }

  archiveFile_FilesRollover(requestData: RolloverRequestMessage, signal?: AbortSignal): Promise<boolean> {
    let url_ = this.baseUrl + "/api/v1/ArchiveFile/FilesRollover";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(requestData);

    let options_: RequestInit = {
      body: content_,
      method: "POST",
      signal,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => {
        return this.http.fetch(url_, transformedOptions_);
      })
      .then((_response: Response) => {
        return this.processArchiveFile_FilesRollover(_response);
      });
  }

  protected processArchiveFile_FilesRollover(response: Response): Promise<boolean> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }
    if (status === 200) {
      return response.text().then((_responseText) => {
        let result200: any = null;
        result200 = _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as boolean);
        return result200;
      });
    } else if (status === 400) {
      return response.text().then((_responseText) => {
        let result400: any = null;
        result400 = _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as ProblemDetails);
        return throwException("A server side error occurred.", status, _responseText, _headers, result400);
      });
    } else if (status === 500) {
      return response.text().then((_responseText) => {
        return throwException("A server side error occurred.", status, _responseText, _headers);
      });
    } else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      });
    }
    return Promise.resolve<boolean>(null as any);
  }

  orchestrator_Orchestrator(rolloverRequestMessage: RolloverRequestMessage, signal?: AbortSignal): Promise<boolean> {
    let url_ = this.baseUrl + "/api/v1/Orchestrator/Orchestrator";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(rolloverRequestMessage);

    let options_: RequestInit = {
      body: content_,
      method: "POST",
      signal,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => {
        return this.http.fetch(url_, transformedOptions_);
      })
      .then((_response: Response) => {
        return this.processOrchestrator_Orchestrator(_response);
      });
  }

  protected processOrchestrator_Orchestrator(response: Response): Promise<boolean> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }
    if (status === 200) {
      return response.text().then((_responseText) => {
        let result200: any = null;
        result200 = _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as boolean);
        return result200;
      });
    } else if (status === 400) {
      return response.text().then((_responseText) => {
        let result400: any = null;
        result400 = _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as ProblemDetails);
        return throwException("A server side error occurred.", status, _responseText, _headers, result400);
      });
    } else if (status === 500) {
      return response.text().then((_responseText) => {
        return throwException("A server side error occurred.", status, _responseText, _headers);
      });
    } else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      });
    }
    return Promise.resolve<boolean>(null as any);
  }

  notificationAction(request: NotificationActionRequest, signal?: AbortSignal): Promise<string> {
    let url_ = this.baseUrl + "/api/v1/RolloverTracks/rollover-notification";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(request);

    let options_: RequestInit = {
      body: content_,
      method: "POST",
      signal,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => {
        return this.http.fetch(url_, transformedOptions_);
      })
      .then((response: Response) => {
        return this.processNotificationAction(response);
      });
  }

  protected processNotificationAction(response: Response): Promise<string> {
    const status = response.status;
    let _headers: any = {};

    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }

    if (status === 200) {
      return response.text().then((_responseText) => {
        return _responseText;
      });
    }
    else if (status === 400) {
      return response.text().then((_responseText) => {
        let result400: any = _responseText === "" ? null : JSON.parse(_responseText);
        return throwException("Bad request.", status, _responseText, _headers, result400);
      });
    }
    else if (status === 401) {
      return response.text().then((_responseText) => {
        return throwException("Unauthorized.", status, _responseText, _headers);
      });
    }
    else if (status === 404) {  
      return response.text().then((_responseText) => {
        const message = _responseText || "Resource not found.";
        return throwException(message, status, _responseText, _headers);
      });
    }
    else if (status === 409) {
      return response.text().then((_responseText) => {
        return throwException("Conflict occurred.", status, _responseText, _headers);
      });
    }
    else {
      return response.text().then((_responseText) => {
        return throwException("Unexpected server error.", status, _responseText, _headers);
      });
    }
  }

  getEmailDetails(rolloverTrackId: number, signal?: AbortSignal): Promise<EmailDetailsDto[]> {
    let url_ = this.baseUrl + "/api/v1/RolloverTracks/emailDetails?";
    url_ += "rolloverTrackId=" + encodeURIComponent("" + rolloverTrackId);
    url_ = url_.replace(/[?&]$/, "");

    let options_: RequestInit = {
      method: "GET",
      signal,
      headers: {
        Accept: "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => {
        return this.http.fetch(url_, transformedOptions_);
      })
      .then((response: Response) => {
        return this.processGetEmailDetails(response);
      });
  }

  protected processGetEmailDetails(response: Response): Promise<EmailDetailsDto[]> {
    const status = response.status;
    let _headers: any = {};

    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }

    if (status === 200) {
      return response.text().then((_responseText) => {
        return _responseText === ""
          ? []
          : (JSON.parse(_responseText) as EmailDetailsDto[]);
      });
    }
    else if (status === 401) {
      return response.text().then((_responseText) => {
        return throwException("Unauthorized.", status, _responseText, _headers);
      });
    }
    else if (status === 404) {
      return response.text().then((_responseText) => {
        return throwException("Email details not found.", status, _responseText, _headers);
      });
    }
    else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwException("Unexpected server error.", status, _responseText, _headers);
      });
    }

    return Promise.resolve<EmailDetailsDto[]>([]);
  }

  // Get archived files for restore
  getArchivedFiles(hqSiteId: number, rolloverTrackId: number, signal?: AbortSignal): Promise<ArchivedFileRestoreRequest[]> {
    if (hqSiteId === undefined || hqSiteId === null)
      throw new Error("The parameter 'hqSiteId' must be defined and cannot be null.");
    if (rolloverTrackId === undefined || rolloverTrackId === null)
      throw new Error("The parameter 'rolloverTrackId' must be defined and cannot be null.");

      let url_ = this.baseUrl + `/api/v1/ArchiveFile/GetArchivedFiles/${hqSiteId}/${rolloverTrackId}`;
    url_ = url_.replace(/[?&]$/, "");

    let options_: RequestInit = {
      method: "GET",
      signal,
      headers: {
        Accept: "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => {
        return this.http.fetch(url_, transformedOptions_);
      })
      .then((_response: Response) => {
        return this.processGetArchivedFiles(_response);
      });
  }

  protected processGetArchivedFiles(response: Response): Promise<ArchivedFileRestoreRequest[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }
    if (status === 200) {
      return response.text().then((_responseText) => {
        let result200: any = null;
        result200 =
          _responseText === "" ? [] : (JSON.parse(_responseText, this.jsonParseReviver) as ArchivedFileRestoreRequest[]);
        return result200;
      });
    } else if (status === 400) {
      return response.text().then((_responseText) => {
        let result400: any = null;
        result400 = _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as ProblemDetails);
        return throwException("Bad request.", status, _responseText, _headers, result400);
      });
    } else if (status === 404) {
      return response.text().then((_responseText) => {
        // Return empty array when no archived files found
        return [];
      });
    } else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      });
    }
    return Promise.resolve<ArchivedFileRestoreRequest[]>([]);
  }

  restoreArchiveFiles(request: RestoreArchiveFilesRequest, signal?: AbortSignal): Promise<RestoreArchiveFilesResponse> {
      let url_ = this.baseUrl + "/api/v1/ArchiveFile/RestoreArchiveFiles";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(request);

    let options_: RequestInit = {
      body: content_,
      method: "POST",
      signal,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => {
        return this.http.fetch(url_, transformedOptions_);
      })
      .then((_response: Response) => {
        return this.processRestoreArchiveFiles(_response);
      });
  }

  protected processRestoreArchiveFiles(response: Response): Promise<RestoreArchiveFilesResponse> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }
    if (status === 200) {
      return response.text().then((_responseText) => {
        let result200: any = null;
        result200 =
          _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as RestoreArchiveFilesResponse);
        return result200;
      });
    } else if (status === 400) {
      return response.text().then((_responseText) => {
        let result400: any = null;
        result400 = _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as ProblemDetails);
        return throwException("A server side error occurred.", status, _responseText, _headers, result400);
      });
    } else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      });
    }
    return Promise.resolve<RestoreArchiveFilesResponse>(null as any);
  }

  getRolloverInfo(request: RolloverInfoRequest, signal?: AbortSignal): Promise<RolloverInfoRow[]> {
    let url_ = this.baseUrl + "/api/v1/ArchiveFile/GetRetreivalsList";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(request);

    let options_: RequestInit = {
      body: content_,
      method: "POST",
      signal,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => {
        return this.http.fetch(url_, transformedOptions_);
      })
      .then((_response: Response) => {
        return this.processGetRolloverInfo(_response);
      });
  }

  protected processGetRolloverInfo(response: Response): Promise<RolloverInfoRow[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }
    if (status === 200) {
      return response.text().then((_responseText) => {
        return _responseText === "" ? [] : (JSON.parse(_responseText, this.jsonParseReviver) as RolloverInfoRow[]);
      });
    } else if (status === 400) {
      return response.text().then((_responseText) => {
        let result400: any = null;
        result400 = _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as ProblemDetails);
        return throwException("A server side error occurred.", status, _responseText, _headers, result400);
      });
    } else if (status === 404) {
      return response.text().then(() => []);
    } else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      });
    }
    return Promise.resolve<RolloverInfoRow[]>([]);
  }

  getRetrievalDetailList(rolloverRetrivalId: number, signal?: AbortSignal): Promise<RetrievalDetailRow[]> {
    let url_ = this.baseUrl + `/api/v1/ArchiveFile/GetRetreivalDetailList?rolloverRetrivalId=${encodeURIComponent(rolloverRetrivalId)}`;
    url_ = url_.replace(/[?&]$/, "");

    let options_: RequestInit = {
      method: "GET",
      signal,
      headers: {
        Accept: "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => {
        return this.http.fetch(url_, transformedOptions_);
      })
      .then((_response: Response) => {
        return this.processGetRetrievalDetailList(_response);
      });
  }

  protected processGetRetrievalDetailList(response: Response): Promise<RetrievalDetailRow[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }
    if (status === 200) {
      return response.text().then((_responseText) => {
        return _responseText === "" ? [] : (JSON.parse(_responseText, this.jsonParseReviver) as RetrievalDetailRow[]);
      });
    } else if (status === 404) {
      return response.text().then(() => []);
    } else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      });
    }
    return Promise.resolve<RetrievalDetailRow[]>([]);
  }

  getRetrievalUploadList(rolloverRetrivalId: number, signal?: AbortSignal): Promise<RetrievalUploadRow[]> {
    let url_ = this.baseUrl + `/api/v1/ArchiveFile/GetRetreivalUploadlList?rolloverRetrivalId=${encodeURIComponent(rolloverRetrivalId)}`;
    url_ = url_.replace(/[?&]$/, "");

    let options_: RequestInit = {
      method: "GET",
      signal,
      headers: {
        Accept: "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => {
        return this.http.fetch(url_, transformedOptions_);
      })
      .then((_response: Response) => {
        return this.processGetRetrievalUploadList(_response);
      });
  }

  protected processGetRetrievalUploadList(response: Response): Promise<RetrievalUploadRow[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }
    if (status === 200) {
      return response.text().then((_responseText) => {
        return _responseText === "" ? [] : (JSON.parse(_responseText, this.jsonParseReviver) as RetrievalUploadRow[]);
      });
    } else if (status === 404) {
      return response.text().then(() => []);
    } else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      });
    }
    return Promise.resolve<RetrievalUploadRow[]>([]);
  }

}

function throwException(
  message: string,
  status: number,
  response: string,
  headers: { [key: string]: any },
  result?: any
): any {
  if (result !== null && result !== undefined) throw result;
  else throw new ApiException(message, status, response, headers, null);
}
